import { apiFetch } from "@/integrations/backend/client";

export type Equipment = {
  id: string;
  name: string;
  category: string | null;
  description: string | null;
  instructions: string | null;
  affiliate_url: string | null;
  is_standard: boolean;
  ai_generated?: boolean;
  image_file: string | null;
  is_deleted: boolean;
  deleted_at: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export type EquipmentOption = { id: string; name: string };

export async function listEquipment(): Promise<Equipment[]> {
  const res = await apiFetch("/api/v1/equipment", { method: "GET" });
  if (!res.ok) throw new Error(`equipment_list_failed:${res.status}`);
  const data = (await res.json()) as Equipment[];
  return data ?? [];
}

export async function fetchEquipmentOptions(): Promise<EquipmentOption[]> {
  const equipment = await listEquipment();
  return equipment.map((eq) => ({ id: eq.id, name: eq.name }));
}

export async function softDeleteEquipment(id: string): Promise<void> {
  const res = await apiFetch(`/api/v1/equipment/${id}/soft-delete`, { method: "POST" });
  if (!res.ok) throw new Error(`equipment_soft_delete_failed:${res.status}`);
}

export async function reactivateEquipment(id: string): Promise<void> {
  const res = await apiFetch(`/api/v1/equipment/${id}/reactivate`, { method: "POST" });
  if (!res.ok) throw new Error(`equipment_reactivate_failed:${res.status}`);
}

export async function createEquipment(
  input: {
    name: string;
    description?: string | null;
    category?: string | null;
    instructions?: string | null;
    affiliate_url?: string | null;
    is_standard?: boolean;
  },
  image?: File
): Promise<Equipment> {
  const form = new FormData();
  form.append("name", input.name);
  if (input.description) form.append("description", input.description);
  if (input.category) form.append("category", input.category);
  if (input.instructions) form.append("instructions", input.instructions);
  if (input.affiliate_url !== undefined) form.append("affiliate_url", input.affiliate_url ?? "");
  form.append("is_standard", String(input.is_standard ?? false));
  if (image) {
    form.append("image", image);
  }
  const res = await apiFetch("/api/v1/equipment", {
    method: "POST",
    body: form,
  });
  if (!res.ok) throw new Error(`equipment_create_failed:${res.status}`);
  return (await res.json()) as Equipment;
}

export async function updateEquipment(
  id: string,
  updates: {
    name?: string;
    description?: string | null;
    category?: string | null;
    instructions?: string | null;
    affiliate_url?: string | null;
    is_standard?: boolean;
  },
  image?: File
): Promise<Equipment> {
  const form = new FormData();
  if (updates.name !== undefined) form.append("name", updates.name);
  if (updates.description !== undefined) form.append("description", updates.description ?? "");
  // Only send category if it's explicitly provided (not null/undefined means don't update)
  if (updates.category !== undefined && updates.category !== null) form.append("category", updates.category);
  if (updates.instructions !== undefined) form.append("instructions", updates.instructions ?? "");
  if (updates.affiliate_url !== undefined) form.append("affiliate_url", updates.affiliate_url ?? "");
  if (updates.is_standard !== undefined) form.append("is_standard", String(updates.is_standard));
  if (image) {
    form.append("image", image);
  }
  const res = await apiFetch(`/api/v1/equipment/${id}`, {
    method: "PATCH",
    body: form,
  });
  if (!res.ok) {
    const errorText = await res.text();
    let errorMessage = `equipment_update_failed:${res.status}`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.detail || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }
  return (await res.json()) as Equipment;
}


